import React, { createContext, useContext, ReactNode } from 'react';
import useFlightCalculations from '../hooks/useFlightCalculations';
import { FlightParameters, UnitSystem, OperationalZones } from '../types/calculator';

// Define the context type
interface FlightCalculationsContextType {
  parameters: FlightParameters;
  unitSystem: UnitSystem;
  zones: OperationalZones;
  hasSpaceWarning: boolean;
  setUnitSystem: (unitSystem: UnitSystem) => void;
  setParameters: (params: Partial<FlightParameters>) => void;
  resetCalculations: () => void;
}

// Create the context with a default value
const FlightCalculationsContext = createContext<FlightCalculationsContextType | undefined>(undefined);

// Custom hook for consuming the context
export const useFlightCalculationsContext = () => {
  const context = useContext(FlightCalculationsContext);
  
  if (context === undefined) {
    throw new Error('useFlightCalculationsContext must be used within a FlightCalculationsProvider');
  }
  
  return context;
};

// Provider component
interface FlightCalculationsProviderProps {
  children: ReactNode;
  initialParameters?: FlightParameters;
  initialUnitSystem?: UnitSystem;
}

export const FlightCalculationsProvider: React.FC<FlightCalculationsProviderProps> = ({
  children,
  initialParameters = {
    radius: 30, // Flight Area Radius (default 30m)
    altitude: 121.92, // Altitude (default 121.92m/400ft)
    velocity: 5, // Velocity (Descent Rate) (default 5m/s)
    availableSpace: 75, // Available Space
    unitSystem: 'metric'
  },
  initialUnitSystem = 'metric'
}) => {
  // Use our custom hook for calculations
  const flightCalculations = useFlightCalculations({
    initialParameters,
    initialUnitSystem
  });
  
  // Value to be provided by the context
  const value: FlightCalculationsContextType = {
    parameters: flightCalculations.parameters,
    unitSystem: flightCalculations.unitSystem,
    zones: flightCalculations.zones,
    hasSpaceWarning: flightCalculations.hasSpaceWarning,
    setUnitSystem: flightCalculations.setUnitSystem,
    setParameters: flightCalculations.setParameters,
    resetCalculations: flightCalculations.resetCalculations
  };
  
  return (
    <FlightCalculationsContext.Provider value={value}>
      {children}
    </FlightCalculationsContext.Provider>
  );
};

export default FlightCalculationsProvider;
